/**
 * Conversion by cohort — line-count bucket, customer, or enquiry type.
 *
 * One board with three segments rather than three boards: they are all "cohort → win rate" over a
 * different grouping, drawn from one set of facts.
 *
 * The size segment is the one that pays for the line-weighting elsewhere: it answers whether large
 * enquiries actually convert worse, which is the reason counting enquiries misleads in the first
 * place.
 */
import { useState } from 'react'
import type { ConversionBreakdown } from '../../lib/analytics/types'
import { BoardCard } from './BoardCard'
import { Track, pct } from './charts'

type Segment = 'size' | 'customer' | 'type'

interface Row {
  key: string
  label: string
  enquiries: number
  won: number
  win_rate: number
  extra: string
}

export function ConversionBoard({
  breakdown, isLoading, error, isManager,
}: {
  breakdown: ConversionBreakdown | undefined
  isLoading: boolean
  error: unknown
  isManager: boolean
}) {
  const [segment, setSegment] = useState<Segment>('size')

  // By-customer is manager-only, matching the prototype's gate on per-party analytics.
  const segments: Array<{ id: Segment; label: string }> = [
    { id: 'size', label: 'By enquiry size' },
    ...(isManager ? [{ id: 'customer' as const, label: 'By customer' }] : []),
    { id: 'type', label: 'By type' },
  ]
  const active = segments.some((s) => s.id === segment) ? segment : 'size'

  const rows: Row[] =
    active === 'size'
      ? (breakdown?.by_size ?? []).map((b) => ({
          key: b.bucket,
          label: b.bucket === '0' ? 'no lines' : `${b.bucket} lines`,
          enquiries: b.enquiries,
          won: b.won,
          win_rate: b.win_rate,
          extra: b.avg_cycle_days ? `${b.avg_cycle_days}d avg` : '—',
        }))
      : active === 'customer'
        ? (breakdown?.by_customer ?? []).map((c) => ({
            key: c.customer,
            label: c.customer_name,
            enquiries: c.enquiries,
            won: c.won,
            win_rate: c.win_rate,
            extra: `${c.lines} lines`,
          }))
        : (breakdown?.by_type ?? []).map((t) => ({
            key: t.enquiry_type,
            label: t.enquiry_type,
            enquiries: t.enquiries,
            won: t.won,
            win_rate: t.win_rate,
            extra: `${t.lines} lines`,
          }))

  // Size buckets always come back — all six, most of them empty on a thin dataset. Showing six
  // zero rows is noise, so a segment counts as empty when nothing in it has an enquiry.
  const isEmpty = rows.every((r) => r.enquiries === 0)

  return (
    <BoardCard
      title="Conversion"
      subtitle="Where enquiries turn into wins — and where they stop."
      isLoading={isLoading}
      error={error}
      isEmpty={isEmpty}
      actions={
        <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
          {segments.map((s) => (
            <button
              key={s.id}
              type="button"
              className={active === s.id ? 'btn sm pri' : 'btn sm'}
              onClick={() => setSegment(s.id)}
            >
              {s.label}
            </button>
          ))}
        </div>
      }
    >
      <div style={{ overflowX: 'auto' }}>
        <table style={{ marginTop: 10, width: '100%' }}>
          <thead>
            <tr>
              <th>{active === 'size' ? 'Size' : active === 'customer' ? 'Customer' : 'Type'}</th>
              <th className="right">Enquiries</th>
              <th className="right">Won</th>
              <th className="right">Win rate</th>
              <th className="right">&nbsp;</th>
            </tr>
          </thead>
          <tbody>
            {rows
              // Empty size buckets carry no information; customers and types only appear if real.
              .filter((r) => r.enquiries > 0)
              .map((r) => (
                <tr key={r.key}>
                  <td title={r.key}>{r.label}</td>
                  <td className="right mono">{r.enquiries}</td>
                  <td className="right mono">{r.won}</td>
                  <td className="right mono" style={{ whiteSpace: 'nowrap' }}>
                    {pct(r.win_rate)}
                    <Track value={r.win_rate} max={1} tone="ok" />
                  </td>
                  <td className="right" style={{ color: 'var(--muted)', fontSize: 11.5 }}>{r.extra}</td>
                </tr>
              ))}
          </tbody>
        </table>
      </div>
    </BoardCard>
  )
}
